<template>
  <div class="home-mural-root">
    <div v-permission="'dashboard'">
        <drop-list
          :items="cards"
          @reorder="$event.apply(cards);"
          @insert="insert"
          class="home-dashboard-grid grid grid-cols-12 gap-4 pt-4 pb-8"
        >
          <template v-slot:item="{ item, reorder }">
            <drag
              :key="item.id"
              :data="item"
              @cut="remove(cards, item)"
              handle=".handleCard"
              @dragstart="dragStart(item)"
              @dragend="dragEnd(item)"
              :class="`home-dashboard-grid__item col-span-12 lg:col-span-${item.columns} row-span-${item.rows} self-start w-full`"
            >
              <vs-list class="no-pad w-full">
                <vs-list-item
                  @item-DELETE_ITEM="deleteItem($event)"
                  @item-DUPLICATE_ITEM="duplicateItem($event)"
                  @item-ITEM_UPDATED="updateItem($event)"
                  class="no-pad w-full"
                  :style="reorder ? {} : {}"
                >
                  <div
                    class="handleCard"
                    @mouseenter="handleEnter(item)"
                    @mouseleave="handleLeave(item)"
                  >
                    <v-list-item-avatar>
                      <div v-show="item.handle && !dragging">
                        <font-awesome-icon icon="grip-horizontal" />
                      </div>
                    </v-list-item-avatar>
                  </div>

                  <div
                    v-if="item.type === 'reporting_channel_kpis'"
                    class="relative w-full flex flex-col"
                  >
                    <home-reporting-channel-kpis-card />
                  </div>
                </vs-list-item>
              </vs-list>
            </drag>
          </template>

          <template v-slot:feedback="{ data }">
            <div class="item feedback" :key="data">
              <vue-content-loading :width="300" :height="32">
                <rect x="3" y="5" rx="4" ry="4" width="100" height="7" />
                <rect x="3" y="15" rx="4" ry="4" width="50" height="7" />
                <rect x="3" y="25" rx="4" ry="4" width="75" height="7" />
              </vue-content-loading>
            </div>
          </template>
        </drop-list>
    </div>
  </div>
</template>

<script>
import { Drag, DropList } from 'vue-easy-dnd'
import DashboardService from '@/services/api/DashboardService'
import HomeReportingChannelKpisCard from '@/views/pages/home/HomeReportingChannelKpisCard.vue'

export default {
  components: {
    Drag,
    DropList,
    HomeReportingChannelKpisCard
  },
  data() {
    return {
      dashboardCardService: null,
      dragging: false,
      cards: []
    }
  },
  methods: {
    deleteItem(item) {},
    duplicateItem(sourceQuestion) {},
    updateItem(item) {},
    insert() {},
    remove(list, item) {},
    dragStart(item) {
      this.dragging = true
    },
    dragEnd(item) {
      this.dragging = false
      this.persistCards()
    },
    persistCards() {
      this.cards.forEach((card, index) => {
        card.pos = index
      })
      return this.dashboardService.updateDashboardCards({
        cards: this.cards
      })
    },
    handleEnter(item) {
      item.handle = true
    },
    handleLeave(item) {
      item.handle = false
    },
    /**
     * Inclui o card de KPIs do Formulário de Relato para usuários que ainda não têm
     * (persiste para manter drag-and-drop e banco alinhados).
     */
    /**
     * Remove cards obsoletos (questionários, calendário no mural) e persiste se houve mudança.
     */
    stripObsoleteDashboardCards() {
      const before = this.cards.length
      this.cards = this.cards.filter(
        c => c.type !== 'questionnaires' && c.type !== 'calendar'
      )
      if (this.cards.length === before) {
        return Promise.resolve()
      }
      this.cards.forEach((card, index) => {
        card.pos = index
      })
      return this.persistCards()
    },
    /**
     * Permissão de apresentação (dashboard.reporting_channel_kpis.show): remove o card da
     * grade persistida para não exibir shell vazio de arrastar/soltar.
     */
    removeReportingKpisCardIfNoPresentationPermission() {
      if (!this.$acl || this.$acl.hasPermission('dashboard.reporting_channel_kpis.show')) {
        return Promise.resolve()
      }
      const before = this.cards.length
      this.cards = this.cards.filter(c => c.type !== 'reporting_channel_kpis')
      if (this.cards.length === before) {
        return Promise.resolve()
      }
      this.cards.forEach((card, index) => {
        card.pos = index
      })
      return this.persistCards()
    },
    mergeReportingKpisCardIfNeeded() {
      if (
        !this.$acl ||
        !this.$acl.hasPermission('dashboard.reporting_channel_kpis.show')
      ) {
        return Promise.resolve()
      }
      if (this.cards.some(c => c.type === 'reporting_channel_kpis')) {
        return Promise.resolve()
      }
      const nextId =
        this.cards.reduce(
          (max, c) => Math.max(max, Number(c.id) || 0),
          0
        ) + 1
      this.cards.push({
        id: nextId,
        type: 'reporting_channel_kpis',
        columns: 12,
        rows: 2,
        pos: this.cards.length
      })
      return this.persistCards()
    }
  },
  beforeCreate() {
    this.dashboardService = DashboardService.build(this.$vs)
  },
  beforeRouteLeave(to, from, next) {
    if (this.$vs && this.$vs.loading) {
      this.$vs.loading.close()
    }
    next()
  },
  mounted() {
    this.$vs.loading()
    this.dashboardService
      .loadDashboardCards()
      .then(data => {
        this.cards = Array.isArray(data) ? data : []
        return this.stripObsoleteDashboardCards()
      })
      .then(() => this.removeReportingKpisCardIfNoPresentationPermission())
      .then(() => this.mergeReportingKpisCardIfNeeded())
      .finally(() => {
        if (this.$vs && this.$vs.loading) {
          this.$vs.loading.close()
        }
      })
  },
}
</script>

<style lang="scss">
/* Fluxo no topo da viewport: evita grade esticada (min-h-screen + rows fixas)
   empurrando o conteúdo para o rodapé. */
.home-dashboard-grid {
  grid-auto-rows: min-content;
  align-content: start;
  align-items: start;
}

.home-dashboard-grid__item {
  min-height: 0;
}

.drop-allowed.drop-in * {
  cursor: inherit !important;
}

.handleCard {
  width: 100%;
  height: 20px;
  text-align: center;
  cursor: move;
  position: absolute;
  z-index: 9999999;
  top: 27px;
}

.handleCard > div {
  text-align: center;
}

.vs-list--slot {
  margin: 0;
  width: 100%;
}

.fa-grip-horizontal {
  color: lightgray;
  font-size: 16px;
}

.items-card .vs-button-text {
  font-size: 12px;
}

.item {
  padding: 20px;
  margin-bottom: 10px;
  align-items: center;
  justify-content: center;
}

.feedback {
  border: 2px dashed white;
}

.drag-image {
  transform: translate(-50%, -50%);
}

.panel-item-btn i.vs-icon {
  font-size: 30px;
}

.land {
  @media only screen and (orientation: portrait) and (max-width: 555px) {
    transform: rotate(90deg);
    width: calc(155%);
    .content {
      width: calc(155%);
    }
  }

  .drop-list > .panel-margin {
    margin: 0 5px;
  }
}

.no-pad {
  padding: 0px !important;
}
</style>
